import { BearingEnvelope, RoofContainer } from '@paragon/contract-library';
import { Line2D, LineSegment2D, Polygon2D } from '@paragon/geometry-library';
import {
  calculateBoundedPlane,
  calculateOverhangsAndSubfasciaeForBoundedPlane,
  CeilingTrayGeometry,
  isRoofPlane,
  PlaneWithCalculatedGeometry,
  RoofOrCeilingPlane,
  TrussSolidCalculationDependencies,
} from '@paragon/layout';
import { notNullish, ReadonlyRecord } from '@paragon/utilities';

import { FundamentalLayoutData } from '../../../store/selectors/layoutDataSelectors';
import { FocusedFeaturePatternContainerData } from '../../../store/selectors/layoutGeometricEcosystemSelectors';
import {
  doesWorkLineBoundLevel,
  getBearingAreas,
  getBoundingWorkLineGuids,
  getFeaturePatternContainerData,
  getTrussEnvelopeData,
  getWorkLineGeometries,
  setFocusedObjectDerivedData,
} from './common';
import { GeometricEcosystem, SourcedSnap } from './GeometricEcosystem';
import { TrussEnvelopeData } from './TrussEnvelopeData';

export class RoofPlaneBasedGeometricEcosystem implements GeometricEcosystem {
  readonly source: FundamentalLayoutData;

  readonly roofAreas: readonly Polygon2D[];
  readonly overhangAreas: ReadonlyRecord<string, Polygon2D[]>; // keyed by roof plane guid

  readonly nonOverhangRoofSnaps: readonly SourcedSnap[];
  readonly ceilingSnaps: readonly SourcedSnap[];

  readonly bearingAreas: ReadonlyRecord<string, Polygon2D>; // keyed by bearing envelope guid
  readonly bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>;

  readonly workLines: ReadonlyRecord<string, Line2D | LineSegment2D>; // keyed by work line guid
  readonly boundingWorkLineGuids: ReadonlySet<string>;

  readonly snappingGuideLines: LineSegment2D[];

  readonly trussEnvelopes: ReadonlyRecord<string, TrussEnvelopeData>; // keyed by truss envelope guid

  readonly featurePatternContainers: ReadonlyRecord<string, Polygon2D>; // keyed by feature pattern container guid
  focusedFeaturePatternContainer!: Polygon2D | null;

  constructor(
    source: FundamentalLayoutData, // visible layout objects

    // all bearing envelopes -- needed because of possible cross-layout dependencies
    allBearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,

    // all calculated planes -- needed because of possible cross-layout dependencies
    allCalculatedPlanes: ReadonlyRecord<string, PlaneWithCalculatedGeometry<RoofOrCeilingPlane> | null>,

    roofOutlines: readonly Polygon2D[],
    focusedFeaturePatternContainerData: FocusedFeaturePatternContainerData,
    dependencies: TrussSolidCalculationDependencies,
    snappingGuideLines: LineSegment2D[],
  ) {
    this.source = source;

    this.roofAreas = roofOutlines;

    const overhangAreas: Record<string, Polygon2D[]> = {};
    const roofSnaps: SourcedSnap[] = [];
    const ceilingSnaps: SourcedSnap[] = [];

    const visibleCalculatedRoofPlanes = Object.fromEntries(
      [...Object.keys(this.source.roofPlanes), ...Object.keys(this.source.ceilingPlanes)]
        .map((planeGuid) => allCalculatedPlanes[planeGuid])
        .filter(notNullish)
        .map((roofPlane) => [roofPlane.guid, roofPlane] as const),
    );

    for (const roofPlane of Object.values(visibleCalculatedRoofPlanes)) {
      const snaps: SourcedSnap[] = [];
      snaps.push({ geometry: roofPlane.calculatedGeometry.segment ?? roofPlane.calculatedGeometry.line });

      const bounds = calculateBoundedPlane(roofPlane, allCalculatedPlanes);

      if (bounds.shape.length) {
        const ridgeLines = bounds.shape
          .flatMap(({ edges }) => edges)
          .map((edge) => edge.projectTo2D())
          .filter(notNullish)
          .filter((in2D) =>
            roofPlane.calculatedGeometry.segment
              ? !in2D.overlaps(roofPlane.calculatedGeometry.segment)
              : !in2D.toLine().equals(roofPlane.calculatedGeometry.line),
          )
          .map((geometry): SourcedSnap => ({ geometry }));
        snaps.push(...ridgeLines);
      }

      if (isRoofPlane(roofPlane)) {
        const { overhangs, subfasciae } = calculateOverhangsAndSubfasciaeForBoundedPlane(roofPlane, bounds);

        const overhangArea = overhangs?.map((region) => region.projectTo2D()).filter(notNullish);
        if (overhangArea) overhangAreas[roofPlane.guid] = overhangArea;
        const subfasciaArea = subfasciae?.map((region) => region.projectTo2D()).filter(notNullish);
        if (subfasciaArea) {
          const subfasciaSnaps = subfasciaArea
            .flatMap(({ edges }) => edges)
            .map((geometry): SourcedSnap => ({ geometry, roofPlaneGuid: roofPlane.guid }));
          snaps.push(...subfasciaSnaps);
        }
      }

      if (isRoofPlane(roofPlane)) {
        roofSnaps.push(...snaps);
      } else {
        ceilingSnaps.push(...snaps);
      }
    }

    const ceilingTrayGeometries = Object.values(source.ceilingTrays)
      .map(CeilingTrayGeometry.fromTray)
      .filter(notNullish)
      .flatMap((geometry) => geometry.faces)
      .map((face) => face.projectTo2D())
      .filter(notNullish)
      .flatMap((face) => face.edges)
      .map((geometry): SourcedSnap => ({ geometry }));
    ceilingSnaps.push(...ceilingTrayGeometries);

    this.overhangAreas = overhangAreas;
    this.nonOverhangRoofSnaps = roofSnaps;
    this.ceilingSnaps = ceilingSnaps;

    this.bearingAreas = getBearingAreas(this.source);
    this.bearingEnvelopes = this.source.bearingEnvelopes;
    this.workLines = getWorkLineGeometries(this.source);
    this.boundingWorkLineGuids = getBoundingWorkLineGuids(this.source);

    this.snappingGuideLines = snappingGuideLines;

    this.trussEnvelopes = getTrussEnvelopeData(this.source.trussEnvelopes, dependencies);
    this.featurePatternContainers = getFeaturePatternContainerData(
      this.source.featurePatternContainers,
      allBearingEnvelopes,
    );

    setFocusedObjectDerivedData(focusedFeaturePatternContainerData, allBearingEnvelopes, this);
  }

  getActiveRoofContainer(): RoofContainer {
    throw new Error(`getActiveRoofContainer not implemented for layout based on roof planes`);
  }

  getRoofAreas() {
    return this.roofAreas;
  }

  getOverhangAreas() {
    return Object.values(this.overhangAreas).flat();
  }

  getRoofSnapsWithSources() {
    return this.nonOverhangRoofSnaps.concat(
      Object.entries(this.overhangAreas).flatMap(([roofPlaneGuid, polygons]) =>
        polygons.flatMap(({ edges }) => edges).map((geometry): SourcedSnap => ({ geometry, roofPlaneGuid })),
      ),
    );
  }

  getCeilingSnapsWithSources(): readonly SourcedSnap[] {
    return this.ceilingSnaps;
  }

  getTrussEnvelopes() {
    return Object.values(this.trussEnvelopes);
  }

  getTrussEnvelopeDictionary() {
    return this.trussEnvelopes;
  }

  getBearingAreas() {
    return this.bearingAreas;
  }

  getBearingEnvelopes() {
    return this.bearingEnvelopes;
  }

  getWorkLineDictionary() {
    return this.workLines;
  }

  getBoundingWorkLines(levelGuid: string | null) {
    return Object.entries(this.workLines)
      .filter(
        ([guid]) =>
          this.boundingWorkLineGuids.has(guid) && doesWorkLineBoundLevel(this.source.workLines[guid], levelGuid),
      )
      .map(([_, line]) => line);
  }

  getUploadedSnappingLines(): LineSegment2D[] {
    return this.snappingGuideLines;
  }

  getFeaturePatternContainerDictionaryIn2D() {
    return this.featurePatternContainers;
  }

  getFeaturePatternContainerPolygonsIn2D(): Polygon2D[] {
    return Object.values(this.featurePatternContainers);
  }

  getFocusedFeaturePatternContainerPolygonIn2D(): Polygon2D | null {
    return this.focusedFeaturePatternContainer;
  }
}
